import { useState } from "react";
import { FiSearch, FiX, FiCheck } from "react-icons/fi";
import { MdOutlineBusinessCenter } from "react-icons/md";

import VendorAvatar from "./VendorAvatar";
import { useVendorLookup } from "../../hooks/paid-vendors";
import { useDebounce } from "../../hooks/useDebounce";
import { useSignedUrlMap } from "../../hooks/useSignedUrlMap";

/**
 * Searchable vendor list, fully controlled by the caller.
 *
 * Selection lives with the caller as whole vendor objects (not ids) so it can
 * render what was picked without holding a second copy of the search results.
 * `multiple` is the only difference between the add flow (build a batch) and
 * the edit flow (re-point a record at one vendor).
 */
const VendorPicker = ({ selected = [], onChange, multiple = true }) => {
  const [term, setTerm] = useState("");
  const debouncedTerm = useDebounce(term);
  const { data: vendors = [], isFetching, error } = useVendorLookup(debouncedTerm);

  const avatars = useSignedUrlMap(vendors.map((vendor) => vendor.profilePic));
  const selectedIds = new Set(selected.map((vendor) => vendor._id));

  const toggle = (vendor) => {
    if (!multiple) {
      onChange([vendor]);
      return;
    }
    onChange(
      selectedIds.has(vendor._id)
        ? selected.filter((picked) => picked._id !== vendor._id)
        : [...selected, vendor]
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Search vendors by name, email, company or phone"
          className="w-full border border-gray-300 rounded-lg py-2.5 pl-9 pr-9 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
        />
        {term && (
          <button
            type="button"
            onClick={() => setTerm("")}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <FiX />
          </button>
        )}
      </div>

      {multiple && selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((vendor) => (
            <span
              key={vendor._id}
              className="inline-flex items-center gap-1.5 bg-primary8 text-primary rounded-full pl-2.5 pr-1.5 py-1 text-xs font-medium"
            >
              {vendor.name || vendor.email}
              <button
                type="button"
                onClick={() => toggle(vendor)}
                aria-label={`Remove ${vendor.name || vendor.email}`}
                className="p-0.5 rounded-full hover:bg-white/70"
              >
                <FiX className="text-[13px]" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="max-h-[46vh] overflow-y-auto divide-y divide-gray-100">
          {error && (
            <p className="px-4 py-6 text-sm text-red-600 text-center">{error.message}</p>
          )}

          {!error && vendors.length === 0 && (
            <div className="px-4 py-10 text-center">
              <MdOutlineBusinessCenter className="mx-auto text-3xl text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">
                {isFetching ? "Searching vendors..." : "No vendors match that search"}
              </p>
            </div>
          )}

          {vendors.map((vendor) => {
            const isSelected = selectedIds.has(vendor._id);
            return (
              <button
                key={vendor._id}
                type="button"
                disabled={vendor.isPaid}
                onClick={() => toggle(vendor)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                  vendor.isPaid
                    ? "bg-gray-50 cursor-not-allowed"
                    : isSelected
                    ? "bg-primary8"
                    : "hover:bg-gray-50"
                }`}
              >
                <VendorAvatar
                  src={avatars.get(vendor.profilePic)}
                  name={vendor.name}
                  email={vendor.email}
                  size="sm"
                />

                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-medium truncate ${
                      vendor.isPaid ? "text-gray-400" : "text-gray-800"
                    }`}
                  >
                    {vendor.name || "Unnamed vendor"}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{vendor.email}</p>
                  {vendor.organization && (
                    <p className="text-[11px] text-gray-400 truncate">{vendor.organization}</p>
                  )}
                </div>

                {vendor.isPaid ? (
                  <span className="shrink-0 text-[11px] font-medium text-gray-500 bg-gray-200 rounded-full px-2 py-0.5">
                    Already added
                  </span>
                ) : (
                  <span
                    className={`shrink-0 w-5 h-5 border flex items-center justify-center ${
                      multiple ? "rounded-md" : "rounded-full"
                    } ${
                      isSelected
                        ? "bg-primary border-primary text-white"
                        : "border-gray-300 text-transparent"
                    }`}
                  >
                    <FiCheck className="text-[13px]" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <p className="px-3 py-2 bg-gray-50 border-t border-gray-100 text-[11px] text-gray-500">
          {isFetching && "Searching..."}
          {!isFetching &&
            (debouncedTerm
              ? `${vendors.length} match${vendors.length === 1 ? "" : "es"} — narrow the search if the vendor is missing`
              : "Showing the most recently registered vendors — search to find a specific one")}
        </p>
      </div>
    </div>
  );
};

export default VendorPicker;
